import { useCallback, useEffect, useMemo, useState } from 'react';
import { getHackerAddressList } from '@/services/home';
import { HackerDataList } from '@/types/home';
import { ArrowchevronRightIcon } from '@bfe/moly-icons';
import { useFm } from '@web3/base-hooks';
import { formatNumber } from '@web3/base-utils';

import styles from './index.module.scss';

export const useData = () => {
  const [data, setData] = useState<HackerDataList>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [pageNo, setPageNo] = useState<number>(1);
  const [totalListNum, setTotalListNum] = useState<number>(0);
  const pageSize = 10;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const { code, result } = await getHackerAddressList({
        pageNo,
        pageSize,
      });
      if (code === 0 && result) {
        setData(result.dataList || []);
        setTotalListNum(result?.pageInfo?.totalNum || 0);
      } else {
        setData([]);
      }
      setLoading(false);
    } catch {
      setLoading(false);
      setData([]);
    }
  }, [pageNo]);

  const onChangePageNo = (v: number) => {
    setPageNo(v);
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    pageNo,
    totalListNum,
    pageSize,
    onChangePageNo,
  };
};

export const useColumns = () => {
  const fm = useFm();

  const cols = useMemo(() => {
    return [
      {
        accessorKey: 'address',
        header: fm('address'),
        cell: ({ row }) => {
          const { address } = row?.original || {};
          if (!address) return '--';
          const addressStr = `${address?.slice(0, 10)}...${address?.slice(-4)}`;
          return <div className="font-mono">{addressStr}</div>;
        },
      },
      {
        accessorKey: 'chain',
        header: fm('network'),
        cell: ({ row }) => {
          const { chain } = row?.original || {};

          return <div className={styles.network}>{chain || '--'}</div>;
        },
      },
      {
        accessorKey: 'holdingValue',
        header: fm('walletBalanceUsd'),
        cell: ({ row }) => {
          const { holdingValue } = row?.original || {};
          if (!holdingValue) return '--';

          return `$${formatNumber(holdingValue, {
            kmb: false,
            noTrailZero: true,
          })}`;
        },
      },
      {
        accessorKey: 'viewUrl',
        header: fm('action'),
        cell: ({ row }) => {
          const { address } = row?.original || {};
          const baseUrl = 'https://intel.arkm.com/explorer/address';
          return (
            <div
              className={styles.action}
              onClick={() => {
                if (!address) return;
                window.open(`${baseUrl}/${address}`, '_blank');
              }}
            >
              <span>{fm('viewMore')}</span>
              <ArrowchevronRightIcon className={styles.arrow} />
            </div>
          );
        },
      },
    ];
  }, [fm]);

  return {
    cols,
  };
};
